import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ROLE_OPTIONS } from "@/lib/perms";

const ROLE_BADGE_STYLES = {
  admin: "bg-emerald-50 text-emerald-800 border-emerald-200",
  gestor: "bg-sky-50 text-sky-800 border-sky-200",
  nutricionista: "bg-amber-50 text-amber-800 border-amber-200",
  visualizador: "bg-slate-50 text-slate-700 border-slate-200",
};

const emptyForm = { name: "", email: "", password: "", role: "gestor" };

export default function Users() {
  const { user: me, can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const canWrite = can("users:write");

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [pwdUserId, setPwdUserId] = useState(null);
  const [pwdValue, setPwdValue] = useState("");

  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setRows(data);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => { setEditingId(r.id); setForm({ name: r.name, email: r.email, password: "", role: r.role, active: r.active }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/users/${editingId}`, { name: form.name, role: form.role, active: form.active });
        toast.success("Usuário atualizado");
      } else {
        await api.post("/users", { name: form.name, email: form.email, password: form.password, role: form.role });
        toast.success("Usuário cadastrado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const submitPassword = async () => {
    if (pwdValue.length < 6) { toast.error("Senha precisa ter no mínimo 6 caracteres"); return; }
    try {
      await api.put(`/users/${pwdUserId}/password`, { password: pwdValue });
      toast.success("Senha redefinida");
      setPwdUserId(null); setPwdValue("");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("Usuário removido");
      setDeleteId(null); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        title="Usuários da organização"
        subtitle="Cadastre membros da sua empresa e defina o papel de cada um."
        actions={
          canWrite ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} data-testid="add-user-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                  <Plus size={16} className="mr-1" /> Novo usuário
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle>{editingId ? "Editar usuário" : "Novo usuário"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label>Nome *</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editingId} data-testid="user-email-input" />
                  </div>
                  {!editingId && (
                    <div>
                      <Label>Senha inicial *</Label>
                      <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
                    </div>
                  )}
                  <div>
                    <Label>Papel *</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            <div>
                              <div className="font-medium">{r.label}</div>
                              <div className="text-xs text-slate-500">{r.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {editingId && (
                    <div className="flex items-center gap-3">
                      <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active-switch" />
                      <span className="text-sm text-slate-700">Usuário ativo</span>
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                    <Button type="submit" data-testid="user-submit-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">{editingId ? "Salvar" : "Cadastrar"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      <Section>
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Carregando…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="Nenhum usuário" description="Cadastre membros da sua equipe." />
        ) : (
          <div className="overflow-x-auto">
            <table className="mp-table w-full" data-testid="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Papel</th>
                  <th>Status</th>
                  {canWrite && <th className="text-right">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`user-row-${r.id}`}>
                    <td>
                      <div className="font-medium text-slate-900">
                        {r.name}
                        {r.id === me?.id && <span className="text-xs text-slate-500 ml-2">(você)</span>}
                      </div>
                    </td>
                    <td className="text-slate-600">{r.email}</td>
                    <td>
                      <span className={`inline-block text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border ${ROLE_BADGE_STYLES[r.role] || ROLE_BADGE_STYLES.visualizador}`}>
                        {ROLE_LABELS[r.role] || r.role}
                      </span>
                    </td>
                    <td>
                      {r.active
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Ativo</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Inativo</span>}
                    </td>
                    {canWrite && (
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`edit-user-${r.id}`}><Pencil size={15} /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setPwdUserId(r.id); setPwdValue(""); }} data-testid={`reset-pwd-${r.id}`}><KeyRound size={15} /></Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 disabled:opacity-40"
                            disabled={r.id === me?.id}
                            onClick={() => setDeleteId(r.id)}
                            data-testid={`delete-user-${r.id}`}
                          ><Trash2 size={15} /></Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Dialog open={!!pwdUserId} onOpenChange={(o) => !o && setPwdUserId(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Redefinir senha</DialogTitle></DialogHeader>
          <div>
            <Label>Nova senha</Label>
            <Input type="password" minLength={6} value={pwdValue} onChange={(e) => setPwdValue(e.target.value)} data-testid="reset-pwd-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdUserId(null)}>Cancelar</Button>
            <Button onClick={submitPassword} data-testid="reset-pwd-submit" className="bg-emerald-900 hover:bg-emerald-800 text-white">Redefinir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
            <AlertDialogDescription>O acesso deste usuário será revogado imediatamente. Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-user" className="bg-red-600 hover:bg-red-700 text-white">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
